import { useMemo } from 'react'
import { useRetailerId } from 'utils/routing/params.hooks'
import { useGetRetailerCollectionsTreeQuery } from 'domains/retailer-collections/api'
import { RetailerCollectionOwner, SupportedSurface } from 'domains/retailer-collections/utils/types'
import { useDomainGetScopedPathname } from 'domains/retailer-collections/utils/domain/routing'
import { type TreeNode } from 'domains/retailer-collections/components/browse/utils/types'
import { useRetailerCollectionsContext } from 'domains/retailer-collections/utils/retailerCollections.context'
import { type RawTreeNode, toTreeNode } from './tree.utils'

export const useRetailerCollectionsTree = ({ storeConfigurationId }) => {
  const retailerId = useRetailerId()
  const getPathname = useDomainGetScopedPathname()
  const config = useRetailerCollectionsContext()

  // refetch config as well each time we refetch the collection tree. The config
  // contains the current + max # of top level collections allowed, and need this
  // to be up-to-date after certain actions (create / update collections etc.)
  config.refetchConfig()

  const { data, loading, error, refetch } = useGetRetailerCollectionsTreeQuery({
    variables: {
      retailerId,
      storeConfigurationId,
    },
    fetchPolicy: 'no-cache',
    skip: !storeConfigurationId || !retailerId,
  })

  const rawTree = data?.retailerCollectionsServiceGetRetailerCollectionsTree?.retailerCollections

  const tree = useMemo(() => {
    if (loading || error) return

    const getPath = (collectionIds: string[]) => {
      const [l1, l2, l3, l4, l5] = collectionIds

      const path = getPathname('retailer-collections-view', {
        surface: storeConfigurationId,
        l1,
        l2,
        l3,
        l4,
        l5,
      })

      return path
    }

    return (rawTree || []).map(v =>
      toTreeNode(v as RawTreeNode, {
        pathFn: getPath,
        owner: RetailerCollectionOwner.Retailer,
      })
    )
  }, [loading, error, rawTree, getPathname, storeConfigurationId]) as TreeNode[]

  return {
    refetch,
    tree,
    loading,
    error,
  }
}
